import React from 'react';
import { WORKFLOW_STEPS } from '../utils/applicationSteps';

/**
 * ApplicationStepper Component
 * 
 * A horizontal stepper component that displays workflow steps and handles navigation.
 * Shows visual indicators for active, completed, and disabled steps.
 * Responsive design adapts to mobile viewports.
 * 
 * @param {Object} props
 * @param {string} props.currentStep - The currently active step ID
 * @param {string} props.applicationStage - The current backend application stage
 * @param {Function} props.onStepClick - Callback when a step is clicked, receives stepId
 * @param {Function} props.canAccessStep - Function to check if a step is accessible
 * @param {Function} props.isStepCompleted - Function to check if a step is completed
 */
const ApplicationStepper = ({ 
  currentStep, 
  applicationStage,
  onStepClick,
  canAccessStep,
  isStepCompleted
}) => {
  /**
   * Handle step click with accessibility check
   */
  const handleStepClick = (stepId) => {
    if (canAccessStep(stepId)) {
      onStepClick(stepId);
    }
  };

  /**
   * Determine the visual state of a step
   */
  const getStepState = (step) => {
    const isActive = step.id === currentStep;
    const isCompleted = isStepCompleted(step.id);
    const isAccessible = canAccessStep(step.id);
    
    return {
      isActive,
      isCompleted,
      isAccessible,
      isDisabled: !isAccessible
    };
  };

  return (
    <nav 
      className="bg-white border-b border-gray-200 mb-6"
      aria-label="Application workflow progress"
    >
      <div className="max-w-full">
        {/* Desktop stepper - horizontal scrollable layout */}
        <div className="hidden md:block overflow-x-auto px-4 py-4">
          <ol className="flex items-center justify-start min-w-max space-x-2">
            {WORKFLOW_STEPS.map((step, index) => {
              const state = getStepState(step);
              const isLast = index === WORKFLOW_STEPS.length - 1;

              return (
                <li 
                  key={step.id}
                  className="flex items-center flex-shrink-0"
                >
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={state.isDisabled}
                    className={`
                      flex flex-col items-center justify-center min-w-[80px]
                      ${state.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    aria-label={`${step.label} step`}
                    aria-current={state.isActive ? 'step' : undefined}
                  >
                    {/* Step circle */}
                    <div
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        text-sm font-semibold transition-all duration-200
                        ${state.isActive 
                          ? 'bg-blue-600 text-white ring-4 ring-blue-100' 
                          : state.isCompleted
                            ? 'bg-green-500 text-white'
                            : state.isDisabled
                              ? 'bg-gray-200 text-gray-400'
                              : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                        }
                      `}
                    >
                      {state.isCompleted ? (
                        <svg 
                          className="w-5 h-5" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      ) : (
                        <span>{step.order}</span>
                      )}
                    </div>
                    
                    {/* Step label */}
                    <span
                      className={`
                        mt-2 text-xs font-medium text-center whitespace-nowrap
                        ${state.isActive 
                          ? 'text-blue-600' 
                          : state.isCompleted
                            ? 'text-green-600'
                            : state.isDisabled
                              ? 'text-gray-400'
                              : 'text-gray-600'
                        }
                      `}
                    >
                      {step.label}
                    </span>
                  </button>

                  {/* Connector line between steps */}
                  {!isLast && (
                    <div 
                      className={`
                        w-8 h-0.5 mx-1 flex-shrink-0
                        ${state.isCompleted ? 'bg-green-500' : 'bg-gray-300'}
                      `}
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Mobile stepper - horizontal scrollable layout */}
        <div className="md:hidden overflow-x-auto px-4 py-4">
          <ol className="flex items-center space-x-4 min-w-max">
            {WORKFLOW_STEPS.map((step, index) => {
              const state = getStepState(step);
              const isLast = index === WORKFLOW_STEPS.length - 1;

              return (
                <li 
                  key={step.id}
                  className="flex items-center flex-shrink-0"
                >
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={state.isDisabled}
                    className={`
                      flex flex-col items-center min-w-[60px]
                      ${state.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    style={{ minHeight: '44px', minWidth: '44px' }}
                    aria-label={`${step.label} step`}
                    aria-current={state.isActive ? 'step' : undefined}
                  >
                    {/* Step circle - touch-friendly size */}
                    <div
                      className={`
                        w-11 h-11 rounded-full flex items-center justify-center
                        text-sm font-semibold transition-all duration-200
                        ${state.isActive 
                          ? 'bg-blue-600 text-white ring-4 ring-blue-100' 
                          : state.isCompleted
                            ? 'bg-green-500 text-white'
                            : state.isDisabled
                              ? 'bg-gray-200 text-gray-400'
                              : 'bg-gray-300 text-gray-600'
                        }
                      `}
                    >
                      {state.isCompleted ? (
                        <svg 
                          className="w-5 h-5" 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      ) : (
                        <span>{step.order}</span>
                      )}
                    </div>
                    
                    {/* Abbreviated label for mobile */}
                    <span
                      className={`
                        mt-1 text-xs font-medium text-center whitespace-nowrap
                        ${state.isActive 
                          ? 'text-blue-600' 
                          : state.isCompleted
                            ? 'text-green-600'
                            : state.isDisabled
                              ? 'text-gray-400'
                              : 'text-gray-600'
                        }
                      `}
                    >
                      {step.label}
                    </span>
                  </button>

                  {/* Connector line between steps */}
                  {!isLast && (
                    <div 
                      className={`
                        w-8 h-0.5 mx-1 flex-shrink-0
                        ${state.isCompleted ? 'bg-green-500' : 'bg-gray-300'}
                      `}
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </nav>
  );
};

export default ApplicationStepper;
